import { Box, Grid, StyleProps } from '@chakra-ui/react';
import { format, isSameMonth } from 'date-fns';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Text } from '@bonusly/ds-typography';
import { rem } from '@bonusly/ds-utils';

import { useMonthSelectorContext } from './month-selector-context';

export type MonthSelectorGridProps = StyleProps;

type MonthProps = {
	month: Date;
	isSelected: boolean;
	isDisabled: boolean;
	onSelectMonth: (month: Date) => void;
};

export const MonthSelectorGrid: FC<MonthSelectorGridProps> = (props) => {
	const { currentYear, onSelectMonth, value, disableMonthIf } = useMonthSelectorContext();

	// Generate array of months for the current year
	const months = Array.from({ length: 12 }, (_, index) => {
		return new Date(currentYear, index, 1);
	});

	return (
		<Grid maxW={rem(320)} rowGap={2} columnGap={2} templateColumns="repeat(3, 1fr)" {...props}>
			{months.map((month) => {
				const isSelected = value && isSameMonth(month, value);
				const isDisabled = disableMonthIf?.(month) ?? false;

				return <Month key={format(month, 'M-y')} month={month} isSelected={!!isSelected} isDisabled={isDisabled} onSelectMonth={onSelectMonth} />;
			})}
		</Grid>
	);
};

const Month: FC<MonthProps> = ({ month, isSelected, isDisabled, onSelectMonth }) => {
	const { t } = useTranslation('components');

	let textColor = 'text.default';

	if (isSelected) {
		textColor = 'white';
	}

	if (isDisabled) {
		textColor = 'text.disabled';
	}

	const handleClick = () => {
		if (!isDisabled) {
			onSelectMonth(month);
		}
	};

	return (
		<Box
			alignItems="center"
			aria-label={t('date_picker.month_value', { date: month })}
			as="button"
			backgroundColor={isSelected ? 'secondary.bg' : 'transparent'}
			borderRadius="md"
			color={textColor}
			cursor={isDisabled ? 'not-allowed' : 'pointer'}
			display="flex"
			justifyContent="center"
			minH={rem(48)}
			w="100%"
			onClick={handleClick}
			disabled={isDisabled}
			px={3}
			py={2}
			transitionDuration="fast"
			transitionProperty="background-color, color"
			type="button"
			_focus={{
				backgroundColor: isDisabled ? 'transparent' : isSelected ? 'secondary.bg' : 'secondary.bgAlpha',
			}}
			_hover={{
				backgroundColor: isDisabled ? 'transparent' : isSelected ? 'secondary.bg' : 'secondary.bgAlpha',
			}}
			_active={{
				backgroundColor: isDisabled ? 'transparent' : 'secondary.bgActive',
			}}
			{...(isSelected && {
				'data-selected': true,
			})}
		>
			<Text fontSize="sm" fontWeight={isSelected ? 'semibold' : 'medium'}>
				{format(month, 'MMM')}
			</Text>
		</Box>
	);
};
